import React, { useMemo, type ReactElement } from 'react';
import { usePathname } from 'next/navigation';
import { type TBrows } from '@/common/types/analytics.type';
import { selectCartItems, useCartStore } from '@/store/use-cart.store';
import { selectIsTenant, useDomainStore } from '@/store/use-domain.store';
import { Badge } from 'antd';
import classNames from 'classnames';
import AnalyticsWrapper from '@/common/components/analytics-wrapper';
import Button from '@/common/components/button';
import { calculateCartTotalCount } from '@/common/utils/cart-count.utility';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';
import { resolveStorefrontPath } from '@/common/utils/storefront-path.utility';
import CartIcon from '~/public/assets/images/svg/cart.svg';

interface Props {
  id: keyof TBrows;
  iconClassName?: string;
}

export default function CartWidget({ id, iconClassName }: Props): ReactElement {
  const cartItems = useCartStore(selectCartItems);
  const isTenant = useDomainStore(selectIsTenant);
  const vendorSlug = usePathname().split('/')[1];
  const storefrontPath = resolveStorefrontPath(isTenant ? null : vendorSlug);
  const count = useMemo(() => {
    return calculateCartTotalCount(cartItems);
  }, [cartItems]);

  return (
    <Badge
      count={count}
      color="#F30628"
      style={{
        position: 'absolute',
        height: '15px',
        width: '15px',
        fontSize: '8px',
        minWidth: '13px',
        padding: 0,
        right: '0px',
        top: '0px',
        zIndex: 50,
        lineHeight: 2,
      }}
      className="lg:!h-full"
    >
      <AnalyticsWrapper analytics={[id]}>
        <Button
          type="link"
          className="flex h-full w-full items-center justify-center lg:!h-full lg:!w-[44px] lg:!rounded-[10px] lg:!bg-[#F6F6F5]"
          link={`/cart${createQueryStringUtility({ from: storefrontPath })}`}
          id={id}
        >
          <CartIcon
            className={classNames('h-6 w-6 text-black-default', iconClassName)}
          />
        </Button>
      </AnalyticsWrapper>
    </Badge>
  );
}
